/**
 * Settings — /settings
 * Integrations management page. On mount, reads ?integration=hubspot&status=connected
 * to show a success toast after the OAuth redirect chain completes.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { IntegrationsAPI, errorMessage } from '../services/api';

// Typeform logo (simplified wordmark-style icon)
function TypeformLogo() {
    return (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="36" height="36" rx="8" fill="#262627"/>
            <path d="M11 13h14M18 13v10" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
        </svg>
    );
}

// HubSpot orange sprocket SVG logo (simplified)
function HubSpotLogo() {
    return (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="36" height="36" rx="8" fill="#FF7A59"/>
            <path
                d="M22.5 13.5a3 3 0 1 0-6 0 3 3 0 0 0 6 0zM19.5 18v4.5M13.5 22.5h12M13.5 22.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM25.5 22.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
                stroke="#fff"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function relativeTime(iso) {
    if (!iso) return 'never';
    const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (secs < 60)   return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
}

function Toast({ message, type, onClose }) {
    useEffect(() => {
        const t = setTimeout(onClose, 5000);
        return () => clearTimeout(t);
    }, [onClose]);

    const colors = type === 'success'
        ? 'bg-green-50 border-green-400 text-green-800'
        : 'bg-red-50 border-red-400 text-red-800';

    return (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg ${colors}`}>
            <span className="text-sm font-medium">{message}</span>
            <button onClick={onClose} className="ml-2 text-lg leading-none opacity-60 hover:opacity-100">×</button>
        </div>
    );
}

function HubSpotCard({ integration, onConnect, onDisconnect, connecting }) {
    const isActive      = integration?.status === 'active';
    const isError       = integration?.status === 'error';
    const isDisconnected = !integration || integration.status === 'disconnected';

    return (
        <div className={`rounded-xl border p-5 ${isError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                    <HubSpotLogo />
                    <div>
                        <h3 className="font-semibold text-gray-900">HubSpot</h3>
                        {isDisconnected && (
                            <p className="text-sm text-gray-500 mt-0.5">Import contacts as leads automatically every 15 min</p>
                        )}
                        {isActive && (
                            <p className="text-sm text-gray-600 mt-0.5">
                                Connected to{' '}
                                <span className="font-medium">{integration.external_account_name}</span>
                                {' · '}last synced {relativeTime(integration.last_synced_at)}
                            </p>
                        )}
                        {isError && (
                            <p className="text-sm text-red-700 mt-0.5 font-medium">
                                Sync error: {integration.error_message || 'Unknown error'}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {(isDisconnected || isError) && (
                        <button
                            onClick={onConnect}
                            disabled={connecting}
                            className="px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-60 transition-colors"
                        >
                            {connecting ? 'Connecting…' : isError ? 'Reconnect' : 'Connect HubSpot'}
                        </button>
                    )}
                    {(isActive || isError) && (
                        <button
                            onClick={() => onDisconnect(integration.id)}
                            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Disconnect
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function TypeformCard({ integration, typeformInfo, onSetup, onDisconnect, connecting }) {
    const isConnected = !!typeformInfo;
    const [copied, setCopied] = useState(false);

    function copyUrl() {
        if (!typeformInfo?.webhook_url) return;
        navigator.clipboard.writeText(typeformInfo.webhook_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                    <TypeformLogo />
                    <div>
                        <h3 className="font-semibold text-gray-900">Typeform</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {isConnected
                                ? 'Webhook active — submissions arrive as scored leads'
                                : 'Receive form submissions as leads automatically'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {!isConnected && (
                        <button
                            onClick={onSetup}
                            disabled={connecting}
                            className="px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-60 transition-colors"
                        >
                            {connecting ? 'Setting up…' : 'Connect Typeform'}
                        </button>
                    )}
                    {isConnected && integration && (
                        <button
                            onClick={() => onDisconnect(integration.id)}
                            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Disconnect
                        </button>
                    )}
                </div>
            </div>

            {isConnected && (
                <div className="mt-4 space-y-4">
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                            Your Webhook URL
                        </p>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 overflow-hidden text-ellipsis whitespace-nowrap text-gray-700">
                                {typeformInfo.webhook_url}
                            </code>
                            <button
                                onClick={copyUrl}
                                className="shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 transition-colors min-w-[60px]"
                            >
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <div className="rounded-lg bg-gray-50 border border-gray-100 p-4">
                        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                            How to connect
                        </p>
                        <ol className="space-y-2">
                            {[
                                'Open your Typeform → Connect → Webhooks',
                                'Click "Add a webhook"',
                                <>Paste the URL above and click <strong>Save</strong></>,
                                'Submit a test response — it\'ll appear in your dashboard within seconds',
                            ].map((step, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                                    <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-brand-100 text-brand-700 text-xs font-semibold flex items-center justify-center">
                                        {i + 1}
                                    </span>
                                    <span>{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Settings() {
    const [integrations,      setIntegrations]      = useState([]);
    const [loading,           setLoading]           = useState(true);
    const [connecting,        setConnecting]        = useState(false);
    const [connectingTypeform, setConnectingTypeform] = useState(false);
    const [typeformInfo,      setTypeformInfo]      = useState(null);
    const [toast,             setToast]             = useState(null);

    const clearToast = useCallback(() => setToast(null), []);

    const fetchIntegrations = useCallback(async () => {
        try {
            const { integrations: list } = await IntegrationsAPI.list();
            setIntegrations(list);
        } catch (err) {
            setToast({ type: 'error', message: errorMessage(err) });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Check for OAuth redirect result in query string
        const params = new URLSearchParams(window.location.search);
        const integration = params.get('integration');
        const status      = params.get('status');
        const reason      = params.get('reason');

        if (integration === 'hubspot') {
            if (status === 'connected') {
                setToast({ type: 'success', message: 'HubSpot connected successfully!' });
            } else if (status === 'error') {
                setToast({ type: 'error', message: `HubSpot connection failed${reason ? `: ${reason}` : ''}` });
            }
            // Clean up query string without a navigation
            window.history.replaceState({}, '', '/settings');
        }

        fetchIntegrations();
    }, [fetchIntegrations]);

    async function handleConnect() {
        try {
            setConnecting(true);
            const { url } = await IntegrationsAPI.getAuthorizeUrl('hubspot');
            window.location.href = url;
        } catch (err) {
            setConnecting(false);
            setToast({ type: 'error', message: errorMessage(err) });
        }
    }

    async function handleDisconnect(id) {
        try {
            await IntegrationsAPI.disconnect(id);
            await fetchIntegrations();
            setToast({ type: 'success', message: 'HubSpot disconnected.' });
        } catch (err) {
            setToast({ type: 'error', message: errorMessage(err) });
        }
    }

    async function handleSetupTypeform() {
        try {
            setConnectingTypeform(true);
            const data = await IntegrationsAPI.setupTypeform();
            setTypeformInfo(data);
            await fetchIntegrations();
        } catch (err) {
            setToast({ type: 'error', message: errorMessage(err) });
        } finally {
            setConnectingTypeform(false);
        }
    }

    async function handleDisconnectTypeform(id) {
        try {
            await IntegrationsAPI.disconnect(id);
            setTypeformInfo(null);
            await fetchIntegrations();
            setToast({ type: 'success', message: 'Typeform disconnected.' });
        } catch (err) {
            setToast({ type: 'error', message: errorMessage(err) });
        }
    }

    const hubspot = integrations.find(
        (i) => i.provider === 'hubspot' && i.status !== 'disconnected',
    );

    const typeformIntegration = integrations.find(
        (i) => i.provider === 'typeform' && i.status !== 'disconnected',
    );

    // Auto-fetch the webhook URL when the page loads and typeform is already connected
    useEffect(() => {
        if (typeformInfo || !typeformIntegration) return;
        IntegrationsAPI.setupTypeform()
            .then(setTypeformInfo)
            .catch(() => {});
    }, [typeformIntegration, typeformInfo]);

    return (
        <div className="max-w-2xl mx-auto">
            {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}

            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
                <p className="text-sm text-gray-500 mt-1">Manage your account and integrations</p>
            </div>

            <section>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Integrations</h2>

                {loading ? (
                    <div className="space-y-4">
                        {[0, 1].map((i) => (
                            <div key={i} className="rounded-xl border border-gray-200 bg-white p-5 animate-pulse">
                                <div className="flex items-center gap-4">
                                    <div className="w-9 h-9 rounded-lg bg-gray-200" />
                                    <div className="flex-1">
                                        <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                                        <div className="h-3 w-48 bg-gray-100 rounded" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <HubSpotCard
                            integration={hubspot}
                            onConnect={handleConnect}
                            onDisconnect={handleDisconnect}
                            connecting={connecting}
                        />
                        <TypeformCard
                            integration={typeformIntegration}
                            typeformInfo={typeformInfo}
                            onSetup={handleSetupTypeform}
                            onDisconnect={handleDisconnectTypeform}
                            connecting={connectingTypeform}
                        />
                    </div>
                )}
            </section>
        </div>
    );
}
